/**
 * Chat List Page
 * Displays all chat rooms for the current user
 */

import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatList } from "@/components/chat/ChatList";
import { MessageCircle } from "lucide-react";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { GoogleAd } from "@/components/GoogleAd";

interface Chat {
  id: string;
  listing_id: string;
  listing_title: string;
  listing_price_cents: number | null;
  listing_images: string[];
  seller_id: string;
  buyer_id: string;
  seller_name: string;
  buyer_name: string;
  seller_avatar: string | null;
  buyer_avatar: string | null;
  state: string;
  last_msg_at: number;
  last_message: string;
  unread_count: number;
  created_at: number;
}

export default function ChatListPage() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  // Redirect to home if not logged in
  useEffect(() => {
    if (!user) {
      setLocation("/");
    }
  }, [user, setLocation]);

  const { data, isLoading, error } = useQuery<{ chats: Chat[] }>({
    queryKey: ["/api/chat/list"],
    queryFn: async () => {
      if (!user) throw new Error("Not logged in");
      const token = await user.getIdToken();
      const res = await fetch("/api/chat/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch chat list");
      return res.json();
    },
    enabled: !!user,
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  if (!user) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            채팅을 사용하려면 로그인이 필요합니다.
          </p>
        </Card>
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer size="default">
        <div className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            채팅 목록을 불러오는 중 오류가 발생했습니다.
          </p>
        </Card>
      </PageContainer>
    );
  }

  const chats = data?.chats || [];
  const currentUserId = user.uid;

  return (
    <PageContainer size="default">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">채팅</h1>
        <p className="text-muted-foreground text-sm">
          거래 관련 채팅을 확인하세요
        </p>
      </div>

      {/* AdSense 광고 - 상단 */}
      <div className="mb-6">
        <GoogleAd slot="chat-list-top" format="auto" layout="inline" />
      </div>

      {chats.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-2">채팅 내역이 없습니다</p>
          <p className="text-sm text-muted-foreground">
            마켓 상품에서 "채팅하기" 버튼을 눌러 거래를 시작하세요
          </p>
        </Card>
      ) : (
        <>
          <ChatList chats={chats} currentUserId={currentUserId} />
          {/* AdSense 광고 - 하단 */}
          <div className="mt-6">
            <GoogleAd slot="chat-list-bottom" format="auto" layout="inline" />
          </div>
        </>
      )}
    </PageContainer>
  );
}
